import { IS_PRODUCTION } from 'utils/globals';

import { eventsAPI } from 'apis/wos';

import cartModule from 'domains/cart';
import cartBilling from 'domains/cart_billing';
import cartProducts from 'domains/cart_products';

import reporter from 'utils/reporter';

import type { CartProduct } from 'domains/cart_products';
import type { CartBilling } from 'domains/cart_billing';
import type { CheckoutRootState } from 'store/checkout_store';

declare const dataLayer: any[] | undefined;

declare global {
  interface Window {
    orderData: Array<number | undefined>[];
    retailer: string;
    clickId: string | undefined;
    orderKey: string;
    customerID: string;
    uetq: any[];
  }
}

function submitAnalyticsData(state: CheckoutRootState, orderNumber: string) {
  const cps = cartProducts.selectors.selectAll(state);
  const cart = cartModule.selectors.cart(state);
  const billing = cartBilling.selectors.billing(state);

  if (!cart || !billing || !cart.customer_id || !cart.customer_email) return;

  submitAdlucentData(cps, orderNumber, cart.customer_id);
  submitGoogleData(cps, billing, orderNumber, cart.promo_code);
}

/**
 * Not entirely sure how this is being used, but it's copied directly from order confirmation page
 */
function submitAdlucentData(cps: CartProduct[], orderNumber: string, customerId: string) {
  // Don't have anything really testable with this, so just break away if we're not in prod
  if (!IS_PRODUCTION) return;

  try {
    function getCookieValue(a: string) {
      const b = document.cookie.match('(^|;)\\s*' + a + '\\s*=\\s*([^;]+)');
      return b ? b.pop() : '';
    }

    window.orderData = cps.map(cartProductForAdlucent);
    window.retailer = 'sensaria';
    window.clickId = getCookieValue('_adlClkID');
    window.orderKey = orderNumber;
    window.customerID = customerId;

    initAdlScript();
  } catch (e) {
    reporter.captureException(e, { tags: { analytics: 'adlucent' } });
  }
}

function initAdlScript() {
  const adl = document.createElement('script');
  adl.type = 'text/javascript';
  adl.async = true;
  adl.src = document.location.protocol + '//tracking.deepsearch.adlucent.com/adl.js';
  const s = document.getElementsByTagName('script')[0];
  s.parentNode?.insertBefore(adl, s);
}

function submitGoogleData(
  cps: CartProduct[],
  billing: CartBilling,
  orderNumber: string,
  promoCode: string | null
) {
  try {
    if (!dataLayer) return;
    dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object; uses GTM#
    dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: orderNumber,
        affiliation: 'WOS',
        value: billing.subtotal / 100,
        shipping: billing.shipping_total / 100,
        tax: billing.tax / 100,
        coupon: promoCode,
        items: cps.map(cartProductForGoogle),
      },
    });
  } catch (e) {
    reporter.captureException(e, { tags: { analytics: 'google' } });
  }
}

function submitBingData(billing: CartBilling) {
  try {
    const event = {
      revenue_value: billing.subtotal / 100,
      currency: 'USD',
    };

    window.uetq.push('event', '', event);

    reporter.captureMessage('UET Push Successful', { extra: { event } });
  } catch (e) {
    reporter.captureException(e, { tags: { analytics: 'uet' } });
  }
}

function submitMetaConversionData(total: number) {
  return eventsAPI.post('', {
    event: 'Purchase',
    custom_data: {
      currency: 'USD',
      value: total / 100,
    },
  });
}

function cartProductForGoogle(cp: CartProduct) {
  return {
    item_id: cp.product_api_id,
    item_name: cp.name,
    price: cp.pricing ? cp.pricing.subtotal / 100 : undefined,
    quantity: cp.quantity,
  };
}

function cartProductForAdlucent(cp: CartProduct) {
  return [cp.product_api_id, cp.quantity, cp.pricing ? cp.pricing.subtotal / 100 : undefined];
}

export default {
  submitAnalyticsData,
  submitGoogleData,
  submitBingData,
  submitMetaConversionData,
};
